import {
  DesignerSettings,
  ExperienceType,
  ObjectInterface,
  ProductLayout,
  Wizard,
} from '@taopix/taopix-core/dist/types/types';
import {UserInterfaceState} from '../../Components/Context/UserInterface/UserInterfaceContext';
import {UserFlowType} from './UserFlowFacade';
import {Component} from '@taopix/taopix-core';
import {OptionsAndExtrasDisplayStage} from '../../Enums';

export enum UserFlowStep {
  None = 'none',

  // Smart Design Assistant.
  AssistantWelcome = 'assistantwelcome',
  AssistantManageAssets = 'assistantassets',
  AssistantFrontCover = 'assistantfrontcover',
  AssistantBackCover = 'assistantbackcover',
  AssistantSmartSelection = 'assistantsmartselection',
  AssistantPreview = 'assistantpreview',

  // Calendar Assistant
  CalendarAssistant = 'calendarassistant',
  CalendarSettings = 'calendarsettings',

  // Authentication steps.
  SigninSelector = 'signinselector',
  Login = 'login',
  Register = 'register',
  ForgotPassword = 'forgotpassword',
  Authenticated = 'authenticated',
  Guest = 'guest',
  Auth = 'auth',
  Logout = 'logout',
  LogoutConfirmation = 'logoutconfirmation',

  // Product upgrade.
  Upgrade = 'upgrade',

  // Product change
  Change = 'change',

  // flightcheck
  FlightCheck = 'flightcheck',
  FlightCheckInitialise = 'flightcheckinitialise',
  FlightCheckReview = 'flightcheckreview',
  FlightCheckIssueFix = 'flightcheckissuefix',

  // Options & Extras.
  OptionsAndExtras = 'optionsandextras',
  Components = 'components',
  PerfectlyClear = 'perfectlyclear',
  ProjectSummary = 'projectsummary',

  // Order.
  Order = 'order',

  // Save
  Save = 'save',
  ProjectName = 'projectname',

  // Share
  Share = 'share',
  ShareInititialise = 'shareinitialise',

  ForceLogin = 'forcelogin',
}

export type UserFlowList = string[];

export enum UserFlowDirection {
  Forward = 'forward',
  Back = 'back',
  Both = 'both',
}

export abstract class BaseUserFlow {
  static skipStep: Map<string, Set<string>> = new Map([
    [UserFlowDirection.Forward, new Set<string>()],
    [UserFlowDirection.Back, new Set<string>()],
  ]);

  protected step = 0;
  protected steps: string[] = [UserFlowStep.None];
  protected designerSettings: DesignerSettings = {} as DesignerSettings;
  protected productLayout: ProductLayout = {} as ProductLayout;
  protected uiState: UserInterfaceState = {} as UserInterfaceState;
  protected stage = UserFlowStep.None;
  protected experience: Record<string, ObjectInterface> = {
    WIZARD: {} as Wizard,
    EDITOR: {},
    SETTINGS: {},
  };
  protected availableSteps: Record<any, UserFlowStep[]> = {
    [UserFlowType.Assistant]: [
      UserFlowStep.AssistantWelcome,
      UserFlowStep.AssistantManageAssets,
      UserFlowStep.AssistantFrontCover,
      UserFlowStep.AssistantBackCover,
      UserFlowStep.AssistantSmartSelection,
      UserFlowStep.AssistantPreview,
      UserFlowStep.CalendarSettings,
    ],
    [UserFlowType.Auth]: [
      UserFlowStep.SigninSelector,
      UserFlowStep.Login,
      UserFlowStep.ForgotPassword,
      UserFlowStep.Register,
      // UserFlowStep.Guest,
      UserFlowStep.Logout,
      UserFlowStep.LogoutConfirmation,
    ],
    [UserFlowType.Save]: [UserFlowStep.ProjectName, UserFlowStep.Save],
    [UserFlowType.Share]: [UserFlowStep.ShareInititialise, UserFlowStep.Share],
    [UserFlowType.FlightCheck]: [
      UserFlowStep.FlightCheck,
      UserFlowStep.FlightCheckInitialise,
      UserFlowStep.FlightCheckReview,
      UserFlowStep.FlightCheckIssueFix,
    ],
    [UserFlowType.Upgrade]: [UserFlowStep.Upgrade],
    [UserFlowType.OptionsAndExtras]: [
      UserFlowStep.Components,
      UserFlowStep.PerfectlyClear,
      UserFlowStep.ProjectSummary,
    ],
    [UserFlowType.Order]: [UserFlowStep.Order],
    [UserFlowType.Change]: [UserFlowStep.Change],
    [UserFlowType.ForceLogin]: [],
  };
  protected type = UserFlowType.UserFlow;
  protected canCancel = true;
  protected active = false;

  public setUserFlowStage = (stage: UserFlowType) => {};

  public getCanCancel = (): boolean => {
    return this.canCancel;
  };

  public isUserFlowActive = (step: UserFlowType): boolean => {
    return false;
  };

  public isStepActive = (userFlowType: UserFlowType, userFlowStep: UserFlowStep): boolean => {
    return false;
  };

  public getAvailableSteps(): Record<any, UserFlowStep[]> {
    return this.availableSteps;
  }

  /**
   * Implemented by UserFlowFacade.
   */
  public getActiveStages = (): string[] => {
    return [];
  };

  /**
   * Implemented by UserFlowFacade.
   *
   * @param userFlowType Userflow type to set as inactive.
   */
  public setInactive = (userFlowType: UserFlowType) => {};

  /**
   * Implemented by UserFlowFacade.
   *
   * @param userFlowType Userflow type to activate.
   */
  public activateUserFlow = (userFlowType: UserFlowType): void => {};

  /**
   * Implemented by UserFlowFacade.
   *
   * @param userFlowType Userflow type to deactivate.
   */
  public deactivateUserFlow = (userFlowType: UserFlowType): void => {};

  /**
   * Implemented by UserFlowFacade.
   */
  public clearActiveStages = () => {};

  /**
   * Sets the designer settings.
   *
   * @param designerSettings Designer settings to set.
   * @returns UserFlow instance.
   */
  public setDesignerSetting(designerSettings: DesignerSettings): this {
    this.designerSettings = designerSettings;
    return this;
  }

  /**
   * Sets the designer product layout details.
   *
   * @param productLayout Product layout details to set.
   * @returns UserFlow instance.
   */
  public setProductLayout(productLayout: ProductLayout): this {
    this.productLayout = productLayout;
    return this;
  }

  public setUi(uiState: UserInterfaceState): this {
    this.uiState = uiState;
    return this;
  }

  public setExperience(experience: ExperienceType): this {
    this.experience = experience;
    return this;
  }

  public setStage(stage: any): this {
    this.stage = stage;
    return this;
  }

  /**
   * Sets the steps for the user flow.
   *
   * @param steps List of UserFlowSteps.
   * @returns UserFlow instance.
   */
  public setSteps = (steps: UserFlowList) => {
    this.steps = steps;
    return this;
  };

  /**
   * Returns the list of steps for the user flow.
   * @returns Array of UserFlowStep items.
   */
  public getSteps(userFlowType?: UserFlowType): UserFlowList {
    return this.steps;
  }

  /**
   * Set the step (index) of where we are in the list of this.steps.
   *
   * @param step Index of this.steps to set.
   * @returns Array of UserFlowStep items.
   */
  public setStep(step: number) {
    this.step = step;
    return this;
  }

  /**
   * Returns the step (index) of where we are in the list of this.steps.
   *
   * @returns Index position of this.steps.
   */
  public getStep() {
    return this.step;
  }

  public resetStep = () => {
    this.setStep(0);
  };

  /**
   * Increments the value of this.step by 1.
   *
   * @returns New index position of this.steps.
   */
  public setNextStep() {
    let nextStep = this.getStep() + 1;
    if (this.steps[nextStep]) {
      while (BaseUserFlow.skipStep.get(UserFlowDirection.Forward)?.has(this.steps[nextStep])) {
        nextStep++;
      }
    }

    this.setStep(nextStep);
    return nextStep;
  }

  public canChangeStep(step: string): boolean {
    return true;
  }

  /**
   * gets what would be the next step, or until any steps marked to be skipped have been.
   *
   * @returns The new step, or UserFlowStep.None if the end has been reached.
   */
  public getNextStep = (): string => {
    let nextStep = this.getStep() + 1;

    // Return early if there are no more steps.
    if (!this.steps.hasOwnProperty(nextStep)) return `${this.type}.${UserFlowStep.None}`;

    while (BaseUserFlow.skipStep.get(UserFlowDirection.Forward)?.has(this.steps[nextStep])) {
      nextStep++;
    }

    return this.steps.hasOwnProperty(nextStep)
      ? this.steps[nextStep]
      : `${this.type}.${UserFlowStep.None}`;
  };

  /**
   * Decrements the value of this.step by 1.
   *
   * @returns New index position of this.steps.
   */
  public setPreviousStep = () => {
    let previousStep = this.getStep() - 1;

    while (BaseUserFlow.skipStep.get(UserFlowDirection.Back)?.has(this.steps[previousStep])) {
      previousStep--;
    }

    previousStep = Math.max(0, previousStep);

    this.setStep(previousStep);

    return previousStep;
  };

  /**
   * Gets what would be the previous step, or until any steps marked to be skipped have been.
   *
   * @returns The previous step, or UserFlowStep.None if a non-valid value has been reached.
   */
  public getPreviousStep = (): string => {
    let nextStep = Math.max(0, this.getStep() - 1);

    // Return early if there are no more steps.
    if (!this.steps.hasOwnProperty(nextStep)) return `${this.type}.${UserFlowStep.None}`;

    while (BaseUserFlow.skipStep.get(UserFlowDirection.Back)?.has(this.steps[nextStep])) {
      nextStep--;
    }

    return this.steps[nextStep] || `${this.type}.${UserFlowStep.None}`;
  };

  /**
   * Returns what step the user flow is currently at without any replacements.
   *
   * @returns UserFlowStep the user flow is at, or UserFlowStep.None if it is not a valid step.
   */
  public getRealCurrentStep = (): string => {
    return this.steps[this.getStep()] || `${this.type}.${UserFlowStep.None}`;
  };

  /**
   * Returns what step the user flow is currently at.
   *
   * @returns UserFlowStep the user flow is at, or UserFlowStep.None if it is not a valid step.
   */
  public getCurrentStep(userFlowType?: UserFlowType): string {
    return this.steps[this.getStep()] || `${this.type}.${UserFlowStep.None}`;
  }

  /**
   * Sets the current step the user flow is currently at.
   *
   * @returns UserFlowStep the user flow is at, or UserFlowStep.None if it is not a valid step.
   */
  public setCurrentStep = (newStep: string) => {
    const steps = this.getSteps();
    this.setStep(steps.indexOf(`${newStep}` as string));

    return this;
  };

  /**
   * Add a step to be skipped.
   *
   * @param direction What direction the user is moving where the step should be skipped.
   * @param step The step to be skipped.
   * @returns UserFlow instance.
   */
  public addSkipStep(direction: UserFlowDirection, step: string): this {
    if (UserFlowDirection.Both === direction) {
      this.addSkipStep(UserFlowDirection.Back, step);
      this.addSkipStep(UserFlowDirection.Forward, step);
      return this;
    }

    const map = BaseUserFlow.skipStep.get(direction);
    if (map) {
      map.add(step);
    }

    return this;
  }

  /**
   * Removes a skipped step.
   *
   * @param direction What direction the user is moving where the step should be no longer be skipped.
   * @param step The step to be no longer be skipped.
   * @returns UserFlow instance.
   */
  public removeSkipStep(direction: UserFlowDirection, step: string): this {
    if (UserFlowDirection.Both === direction) {
      this.removeSkipStep(UserFlowDirection.Back, step);
      this.removeSkipStep(UserFlowDirection.Forward, step);
      return this;
    }

    const map = BaseUserFlow.skipStep.get(direction);
    if (map) {
      map.delete(step);
    }

    return this;
  }

  /**
   * Returns what to display based on the workflow mode, if not signed in.
   *
   * @returns The UserFlowStep.Upgrade step, or null if the dialog can't be shown.
   */
  protected getGuestWorkflowModeFlow = (): UserFlowStep | null => {
    if (!this.designerSettings.authentication.canSignIn) return null;
    if (this.designerSettings.authentication.userRegistered) return null;
    const {experience, uiState} = this;

    if (experience.SETTINGS.guestAccess.allowGuestAccess) {
      if (experience.SETTINGS.guestAccess.promptGuestsToSignIn && !uiState.projectChanged) {
        return UserFlowStep.SigninSelector;
      }
      return null;
    } else {
      if (!experience.SETTINGS.guestAccess.allowGuestAccess) return UserFlowStep.Login;
      if (!experience.SETTINGS.guestAccess.allowGuestsToRegister) return UserFlowStep.Login;
      return UserFlowStep.SigninSelector;
    }
  };

  /**
   * Returns if the upgrade prompt needs to be shown.
   *
   * @returns The UserFlowStep.Upgrade step, or null if the sigin in can't be shown.
   */
  protected getPromptUpgradeOnOpen = (): UserFlowStep | null => {
    const {experience, uiState} = this;
    return experience.EDITOR.upgradeProject.showUpgradeProjectPromptOnOpen &&
      this.productLayout.upgradelayoutref > 0 &&
      !uiState.projectChanged
      ? UserFlowStep.Upgrade
      : null;
  };

  /**
   * Returns if the Options & Extras dialog needs to be shown.
   *
   * @returns The UserFlowStep.OptionsAndExtras step, or null if the dialog can't be shown.
   */
  protected getOptionsAndExtrasOnOpen = (): UserFlowStep | null => {
    const {price} = this.designerSettings;

    const productComponents =
      price.pricing !== null &&
      price.pricing.components.hasOwnProperty('PRODUCT') &&
      this.experience.EDITOR.componentOptions.showComponentOptionsOnOpen
        ? Component.flattenComponentTree(
            Object.values((price.pricing.components.PRODUCT || {children: []}).children)
          ).filter(component => {
            return (
              ['TAOPIXAI', 'CALENDARCUSTOMISATION'].indexOf(component.sectioncode) === -1 &&
              ((component.displaystage as OptionsAndExtrasDisplayStage) &
                OptionsAndExtrasDisplayStage.Start) ===
                OptionsAndExtrasDisplayStage.Start
            );
          })
        : [];

    if (productComponents.length > 0) {
      return UserFlowStep.OptionsAndExtras;
    }

    return null;
  };

  /**
   * Builds a list of steps for the user flow.
   *
   * @returns List of available steps.
   */
  public configure(): UserFlowList {
    // Possible states.
    return [];
  }
}
